import type { ParsedCartItem } from 'ui/types/cartTypes';
import Image from 'next/image';
import QuantitySelector from './QuantitySelector';
import { LayeredImage } from 'ui/components/LayeredImage/LayeredImage';

interface ICartItemProps {
  item: ParsedCartItem;
  formatDisplayPrice: (price: number) => string;
  cartId: string;
}

export const getCartItemDisplayImage = (
  item: ParsedCartItem,
): string | undefined => {
  return item.imageUrls?.[0];
};

const CartItem = ({ item, formatDisplayPrice, cartId }: ICartItemProps) => {
  return (
    <div className='mb-4 grid grid-cols-3 items-start gap-4 last:mb-0'>
      {/* Column 1: Image */}
      {item.imageUrls.length > 1 ? (
        <div className='h-20 w-20 rounded-lg bg-gray-50 object-contain'>
          <LayeredImage images={item.imageUrls} />
        </div>
      ) : (
        <Image
          alt={item.title}
          className='h-20 w-20 rounded-lg bg-gray-50 object-contain'
          height={80}
          src={getCartItemDisplayImage(item) ?? ''}
          width={80}
        />
      )}
      {/* Column 2: Title, Description, SKU */}
      <div>
        <p className='product-title text-sm font-semibold'>{item.title}</p>
        <div className='product-description mt-1 text-sm text-gray-600'>
          {item.description.map((desc, index) => (
            <p className='text-gray-a100' key={index}>
              {desc}
            </p>
          ))}
        </div>
      </div>
      {/* Column 3: Price, Quantity */}
      <div className='flex flex-col place-content-between items-end space-y-2'>
        <div className='text-right'>
          {item.discountedPrice ? (
            <>
              <p className='product-price text-gray-500 line-through'>
                {formatDisplayPrice(item.originalPrice.centAmount)}
              </p>
              <p className='product-price font-semibold'>
                {formatDisplayPrice(item.discountedPrice.value.centAmount)}
              </p>
            </>
          ) : (
            <p className='product-price font-semibold'>
              {formatDisplayPrice(item.originalPrice.centAmount)}
            </p>
          )}
        </div>
        <QuantitySelector
          cartId={cartId}
          itemId={item.id}
          max={item.maxQuantity}
          min={item.minQuantity}
          quantity={item.quantity}
        />
      </div>
    </div>
  );
};

export default CartItem;
